<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { dashboardService } from "@/services/dashboardService";

import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/examples/Charts/GradientLineChart.vue";
import CategoriesList from "./components/CategoriesList.vue";
import TimelineList from "@/examples/Cards/TimelineList.vue";
import TimelineItem from "@/examples/Cards/TimelineItem.vue";

/*
|--------------------------------------------------------------------------
| Dashboard state
|--------------------------------------------------------------------------
|
| Docelowo cały ten obiekt będzie pochodził z:
|
| GET /api/dashboard
|
| Na tym etapie dane są przykładowe, żeby zbudować kompletny layout.
|
*/

const selectedPeriod = ref("30d");

const dashboardData = ref({
  sales: { today: 0, previousDay: 0, month: 0, previousMonth: 0 },
  orders: { today: 0, new: 0, waitingStock: 0, inProduction: 0, ready: 0, shipped: 0 },
  inventory: { stockValue: 0, variants: 0, lowStock: 0, outOfStock: 0, reserved: 0 },
  production: { pending: 0, inProgress: 0, completedToday: 0, failed: 0 },
  profitability: { grossMargin: 0, grossProfit: 0 },
  salesChart: { labels: [], values: [] },
  productionQueue: [],
  lowStock: [],
  activity: [],
});

const loading = ref(false);
const error = ref(null);

function camelCaseKey(key) {
  return key.charAt(0).toLowerCase() + key.slice(1);
}

function normalizeApiValue(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeApiValue);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        camelCaseKey(key),
        normalizeApiValue(nestedValue),
      ])
    );
  }

  // API zwraca wartości liczbowe jako stringi, np. "1517.29".
  if (typeof value === "string" && value.trim() !== "" && /^-?\d+(\.\d+)?$/.test(value)) {
    return Number(value);
  }

  return value;
}

function normalizeActivityItem(item, index) {
  const activity = item ?? {};
  const rawIcon = activity.icon;

  return {
    ...activity,
    title: activity.title ?? activity.name ?? activity.event ?? `Aktywność #${index + 1}`,
    dateTime: activity.dateTime ?? activity.createdAt ?? activity.date ?? activity.timestamp ?? "",
    description: activity.description ?? activity.details ?? activity.message ?? "",
    color: activity.color ?? "primary",
    // TimelineItem oczekuje obiektu, nie stringa.
    icon: rawIcon && typeof rawIcon === "object"
      ? rawIcon
      : {
          component: rawIcon || "ni ni-bell-55",
          background: activity.color ?? "primary",
        },
    badges: activity.badges ?? [],
  };
}

function normalizeDashboardData(data) {
  const normalized = normalizeApiValue(data ?? {});

  const sales = normalized.sales ?? {};
  const orders = normalized.orders ?? {};
  const inventory = normalized.inventory ?? {};
  const production = normalized.production ?? {};
  const profitability = normalized.profitability ?? {};
  const salesChart = normalized.salesChart ?? {};

  const normalizedLowStock = (normalized.lowStock ?? []).map((item, index) => ({
    ...item,

    // Nazwy używane przez tabelę w Dashboard.vue.
    // API zwraca: ItemName, ItemSku, VariantSku, QuantityOnHand...
    id: item.variantId ?? item.id ?? index,
    variant:
      item.variantName ??
      item.itemName ??
      item.productName ??
      item.variantSku ??
      "—",
    sku:
      item.variantSku ??
      item.itemSku ??
      item.sku ??
      "—",
    stock:
      item.quantityOnHand ??
      item.stock ??
      item.quantity ??
      0,
    reserved:
      item.quantityReserved ??
      item.reserved ??
      0,
    available:
      item.quantityAvailable ??
      item.available ??
      (
        Number(item.quantityOnHand ?? item.stock ?? 0) -
        Number(item.quantityReserved ?? item.reserved ?? 0)
      ),
  }));

  const normalizedProductionQueue = (normalized.productionQueue ?? []).map(
    (job, index) => ({
      ...job,
      id: job.id ?? job.productionJobId ?? index,
      number: job.number ?? job.jobNumber ?? job.productionNumber ?? "—",
      variant:
        job.variant ??
        job.variantName ??
        job.productName ??
        job.itemName ??
        "—",
      sku: job.sku ?? job.variantSku ?? job.itemSku ?? "—",
      produced: job.produced ?? job.quantityProduced ?? 0,
      quantity: job.quantity ?? job.quantityPlanned ?? 0,
      status: job.status ?? job.state ?? "PENDING",
    })
  );

  const normalizedActivity = (normalized.activity ?? []).map(
    normalizeActivityItem
  );

  return {
    sales: {
      today: sales.today ?? 0,
      previousDay: sales.previousDay ?? 0,

      // API: Period / PreviousPeriod
      month: sales.period ?? sales.month ?? 0,
      previousMonth: sales.previousPeriod ?? sales.previousMonth ?? 0,

      currency: sales.currency ?? "PLN",
      periodChangePercent: sales.periodChangePercent ?? 0,
      todayChangePercent: sales.todayChangePercent ?? 0,
    },

    orders: {
      today: orders.today ?? 0,
      new: orders.new ?? 0,
      waitingStock: orders.waitingStock ?? 0,
      inProduction: orders.inProduction ?? 0,
      ready: orders.ready ?? 0,
      shipped: orders.shipped ?? 0,
      cancelled: orders.cancelled ?? 0,
      completed: orders.completed ?? 0,
    },

    inventory: {
      stockValue: inventory.stockValue ?? 0,
      variants: inventory.variants ?? 0,
      lowStock: inventory.lowStock ?? 0,
      outOfStock: inventory.outOfStock ?? 0,
      reserved: inventory.reserved ?? 0,
    },

    production: {
      pending: production.pending ?? 0,
      inProgress: production.inProgress ?? 0,
      completedToday: production.completedToday ?? 0,
      failed: production.failed ?? 0,
    },

    profitability: {
      revenue: profitability.revenue ?? 0,
      productCost: profitability.productCost ?? 0,
      additionalCost: profitability.additionalCost ?? 0,
      grossProfit: profitability.grossProfit ?? 0,
      netProfit: profitability.netProfit ?? 0,

      // API zwraca MarginPercent, a frontend używa grossMargin.
      grossMargin:
        profitability.marginPercent ??
        profitability.grossMargin ??
        (
          Number(profitability.revenue ?? 0) > 0
            ? (
                Number(profitability.grossProfit ?? 0) /
                Number(profitability.revenue ?? 0)
              ) * 100
            : 0
        ),
    },

    salesChart: {
      currency: salesChart.currency ?? "PLN",
      labels: Array.isArray(salesChart.labels)
        ? salesChart.labels
        : [],
      values: Array.isArray(salesChart.values)
        ? salesChart.values.map((value) => Number(value ?? 0))
        : [],
    },

    productionQueue: normalizedProductionQueue,
    lowStock: normalizedLowStock,
    activity: normalizedActivity,
  };
}

async function loadDashboard() {
  loading.value = true;
  error.value = null;

  try {
    const days = Number(selectedPeriod.value.replace("d", ""));
    const response = await dashboardService.getDashboard(days);
    const result = response?.data;

    console.log('loadDashboard:',result);
    const success = result?.Success ?? result?.success;
    const data = result?.Data ?? result?.data;
    const message = result?.Message ?? result?.message;

    if (success === false) {
      throw new Error(message || "Nie udało się pobrać dashboardu.");
    }

    if (!data) {
      throw new Error("API nie zwróciło danych dashboardu.");
    }

    // Pełne mapowanie odpowiedzi API PascalCase na model używany przez Vue.
  
    dashboardData.value = normalizeDashboardData(data);
    
    console.log('dashboardData.value',dashboardData.value);

  } catch (err) {
    console.error("Dashboard load error:", err);
    error.value =
      err?.response?.data?.Message ||
      err?.response?.data?.message ||
      err?.message ||
      "Błąd podczas pobierania dashboardu.";
  } finally {
    loading.value = false;
  }
}

onMounted(loadDashboard);
watch(selectedPeriod, loadDashboard);

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

const moneyFormatter = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  minimumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("pl-PL");

function money(value) {
  return moneyFormatter.format(value);
}

function number(value) {
  return numberFormatter.format(value);
}

function percentageChange(current, previous) {
  if (!previous) return 0;

  return ((current - previous) / previous) * 100;
}

function percentageText(value) {
  const rounded = Math.abs(value).toFixed(1);

  return value >= 0
    ? `<span class="text-sm font-weight-bolder text-success">+${rounded}%</span>`
    : `<span class="text-sm font-weight-bolder text-danger">-${rounded}%</span>`;
}

function productionStatus(status) {
  switch (status) {
    case "PENDING":
      return {
        label: "Oczekuje",
        class: "warning",
      };

    case "IN_PROGRESS":
      return {
        label: "W produkcji",
        class: "primary",
      };

    case "COMPLETED":
      return {
        label: "Zakończone",
        class: "success",
      };

    case "FAILED":
      return {
        label: "Błąd",
        class: "danger",
      };

    default:
      return {
        label: status,
        class: "secondary",
      };
  }
}

/*
|--------------------------------------------------------------------------
| Computed
|--------------------------------------------------------------------------
*/

const salesChange = computed(() =>
  percentageChange(
    Number(dashboardData.value.sales?.month ?? 0),
    Number(dashboardData.value.sales?.previousMonth ?? 0)
  )
);

const availableStock = computed(() => {
  return (
    Number(dashboardData.value.inventory?.variants ?? 0) -
    Number(dashboardData.value.inventory?.outOfStock ?? 0)
  );
});

const actionRequired = computed(() => {
  return (
    Number(dashboardData.value.orders?.waitingStock ?? 0) +
    Number(dashboardData.value.inventory?.lowStock ?? 0) +
    Number(dashboardData.value.production?.failed ?? 0)
  );
});

const orderPipeline = computed(() => [
  {
    icon: {
      component: "ni ni-bullet-list-67",
      background: "dark",
    },
    label: "Nowe zamówienia",
    description: `${dashboardData.value.orders.new} oczekujących na realizację`,
  },
  {
    icon: {
      component: "ni ni-watch-time",
      background: "warning",
    },
    label: "Brak magazynu",
    description: `${dashboardData.value.orders.waitingStock} zamówienia wymagają produkcji`,
  },
  {
    icon: {
      component: "ni ni-settings-gear-65",
      background: "primary",
    },
    label: "W produkcji",
    description: `${dashboardData.value.orders.inProduction} zamówień jest aktualnie produkowanych`,
  },
  {
    icon: {
      component: "ni ni-check-bold",
      background: "success",
    },
    label: "Gotowe",
    description: `${dashboardData.value.orders.ready} zamówień gotowych do wysyłki`,
  },
]);

const inventoryOverview = computed(() => [
  {
    icon: {
      component: "ni ni-box-2",
      background: "primary",
    },
    label: "Wartość magazynu",
    description: `<strong>${money(
      dashboardData.value.inventory.stockValue
    )}</strong> aktualnej wartości zapasu`,
  },
  {
    icon: {
      component: "ni ni-single-copy-04",
      background: "success",
    },
    label: "Warianty",
    description: `<strong>${number(
      dashboardData.value.inventory.variants
    )}</strong> wariantów w magazynie`,
  },
  {
    icon: {
      component: "ni ni-bell-55",
      background: "warning",
    },
    label: "Niski stan",
    description: `<strong>${number(
      dashboardData.value.inventory.lowStock
    )}</strong> wariantów poniżej minimum`,
  },
  {
    icon: {
      component: "ni ni-fat-remove",
      background: "danger",
    },
    label: "Brak towaru",
    description: `<strong>${number(
      dashboardData.value.inventory.outOfStock
    )}</strong> wariantów bez dostępnego zapasu`,
  },
]);

const salesChart = computed(() => ({
  labels: dashboardData.value.salesChart.labels,
  datasets: [
    {
      label: "Sprzedaż brutto",
      data: dashboardData.value.salesChart.values,
    },
  ],
}));
</script>

<template>
  <div class="py-4 container-fluid">
    <div v-if="loading" class="alert alert-info text-sm">
      Ładowanie danych dashboardu...
    </div>

    <div
      v-if="error"
      class="alert alert-danger d-flex justify-content-between align-items-center"
    >
      <span>{{ error }}</span>
      <button
        class="btn btn-sm btn-outline-danger mb-0"
        @click="loadDashboard"
      >
        Spróbuj ponownie
      </button>
    </div>
    <!-- ================================================================
         HEADER
    ================================================================= -->

    <div class="row mb-3">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <h5 class="mb-1">Dashboard</h5>
            <p class="mb-0 text-sm text-secondary">
              Przegląd sprzedaży, zamówień, magazynu i produkcji
            </p>
          </div>

          <div class="btn-group">
            <button
              class="btn btn-sm"
              :class="
                selectedPeriod === '7d'
                  ? 'btn-dark'
                  : 'btn-outline-secondary'
              "
              @click="selectedPeriod = '7d'"
            >
              7 dni
            </button>

            <button
              class="btn btn-sm"
              :class="
                selectedPeriod === '30d'
                  ? 'btn-dark'
                  : 'btn-outline-secondary'
              "
              @click="selectedPeriod = '30d'"
            >
              30 dni
            </button>

            <button
              class="btn btn-sm"
              :class="
                selectedPeriod === '90d'
                  ? 'btn-dark'
                  : 'btn-outline-secondary'
              "
              @click="selectedPeriod = '90d'"
            >
              90 dni
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================
         KPI - SALES / ORDERS / MARGIN / ACTIONS
    ================================================================= -->

    <div class="row">
      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Sprzedaż dzisiaj"
          :value="money(dashboardData.sales.today)"
          :description="`${percentageText(
            percentageChange(
              dashboardData.sales.today,
              dashboardData.sales.previousDay
            )
          )} vs poprzedni dzień`"
          :icon="{
            component: 'ni ni-money-coins',
            background: 'bg-gradient-success',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Zamówienia dzisiaj"
          :value="dashboardData.orders.today"
          :description="`<span class='text-sm font-weight-bolder text-primary'>${dashboardData.orders.new}</span> nowych zamówień`"
          :icon="{
            component: 'ni ni-cart',
            background: 'bg-gradient-primary',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Marża brutto"
          :value="`${Number(dashboardData.profitability?.grossMargin ?? 0).toFixed(1)}%`"
          :description="`Zysk brutto: <strong>${money(
            dashboardData.profitability.grossProfit
          )}</strong>`"
          :icon="{
            component: 'ni ni-chart-bar-32',
            background: 'bg-gradient-info',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Wymaga uwagi"
          :value="actionRequired"
          :description="`<span class='text-sm font-weight-bolder text-danger'>${dashboardData.inventory.lowStock}</span> magazyn · <span class='text-sm font-weight-bolder text-warning'>${dashboardData.orders.waitingStock}</span> zamówienia`"
          :icon="{
            component: 'ni ni-bell-55',
            background: 'bg-gradient-warning',
            shape: 'rounded-circle',
          }"
        />
      </div>
    </div>

    <!-- ================================================================
         SECONDARY KPI
    ================================================================= -->

    <div class="row">
      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Wartość magazynu"
          :value="money(dashboardData.inventory.stockValue)"
          :description="`${number(
            dashboardData.inventory.variants
          )} wariantów`"
          :icon="{
            component: 'ni ni-box-2',
            background: 'bg-gradient-dark',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Zarezerwowany stock"
          :value="number(dashboardData.inventory.reserved)"
          description="szt. zarezerwowanych dla zamówień"
          :icon="{
            component: 'ni ni-lock-circle-open',
            background: 'bg-gradient-secondary',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Produkcja w toku"
          :value="dashboardData.production.inProgress"
          :description="`${dashboardData.production.pending} zadań oczekuje`"
          :icon="{
            component: 'ni ni-settings-gear-65',
            background: 'bg-gradient-primary',
            shape: 'rounded-circle',
          }"
        />
      </div>

      <div class="col-xl-3 col-md-6 col-12">
        <mini-statistics-card
          title="Produkcja dzisiaj"
          :value="dashboardData.production.completedToday"
          :description="`<span class='text-sm font-weight-bolder text-success'>${dashboardData.production.completedToday}</span> zakończonych zadań`"
          :icon="{
            component: 'ni ni-check-bold',
            background: 'bg-gradient-success',
            shape: 'rounded-circle',
          }"
        />
      </div>
    </div>

    <!-- ================================================================
         SALES CHART + ORDER PIPELINE
    ================================================================= -->

    <div class="row mt-2">
      <div class="col-lg-8 mb-lg-0 mb-4">
        <div class="card z-index-2 h-100">
          <gradient-line-chart
            id="dashboard-sales-chart"
            title="Sprzedaż brutto"
            :description="`<i class='fa fa-arrow-up text-success'></i>
              <span class='font-weight-bold'>${salesChange.toFixed(
                1
              )}%</span> względem poprzedniego okresu`"
            :chart="salesChart"
          />
        </div>
      </div>

      <div class="col-lg-4">
        <categories-list
          title="Pipeline zamówień"
          :categories="orderPipeline"
        />
      </div>
    </div>

    <!-- ================================================================
         INVENTORY + PRODUCTION SUMMARY
    ================================================================= -->

    <div class="row mt-4">
      <div class="col-lg-5 mb-lg-0 mb-4">
        <categories-list
          title="Magazyn"
          :categories="inventoryOverview"
        />
      </div>

      <div class="col-lg-7">
        <div class="card h-100">
          <div class="p-3 pb-0 card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-1">Kolejka produkcyjna</h6>
                <p class="mb-0 text-sm text-secondary">
                  Zadania ProductionJob wymagające realizacji
                </p>
              </div>

              <router-link
                to="/production/jobs"
                class="btn btn-sm btn-outline-primary mb-0"
              >
                Wszystkie
              </router-link>
            </div>
          </div>

          <div class="card-body px-3 pt-2">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Produkcja
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Produkt
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Ilość
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="job in dashboardData.productionQueue"
                    :key="job.id"
                  >
                    <td>
                      <span class="text-xs font-weight-bold">
                        {{ job.number }}
                      </span>

                      <br />

                      <span class="text-xs text-secondary">
                        #{{ job.id }}
                      </span>
                    </td>

                    <td>
                      <span class="text-sm font-weight-bold">
                        {{ job.variant }}
                      </span>

                      <br />

                      <span class="text-xs text-secondary">
                        {{ job.sku }}
                      </span>
                    </td>

                    <td>
                      <span class="text-sm font-weight-bold">
                        {{ job.produced }} / {{ job.quantity }}
                      </span>
                    </td>

                    <td>
                      <span
                        class="badge badge-sm"
                        :class="`bg-gradient-${
                          productionStatus(job.status).class
                        }`"
                      >
                        {{ productionStatus(job.status).label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================
         LOW STOCK + ACTIVITY
    ================================================================= -->

    <div class="row mt-4">
      <div class="col-lg-7 mb-lg-0 mb-4">
        <div class="card">
          <div class="p-3 pb-0 card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-1">Niski stan magazynowy</h6>
                <p class="mb-0 text-sm text-secondary">
                  Warianty wymagające uzupełnienia
                </p>
              </div>

              <router-link
                to="/inventory-stock"
                class="btn btn-sm btn-outline-warning mb-0"
              >
                Magazyn
              </router-link>
            </div>
          </div>

          <div class="card-body px-3 pt-2">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Produkt
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      SKU
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
                    >
                      Stan
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
                    >
                      Rezerwacja
                    </th>

                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
                    >
                      Dostępne
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="item in dashboardData.lowStock"
                    :key="item.sku"
                  >
                    <td>
                      <span class="text-sm font-weight-bold">
                        {{ item.variant }}
                      </span>
                    </td>

                    <td>
                      <span class="text-xs text-secondary">
                        {{ item.sku }}
                      </span>
                    </td>

                    <td class="text-center">
                      <span class="text-sm font-weight-bold">
                        {{ item.stock }}
                      </span>
                    </td>

                    <td class="text-center">
                      <span class="text-sm">
                        {{ item.reserved }}
                      </span>
                    </td>

                    <td class="text-center">
                      <span
                        class="badge badge-sm"
                        :class="
                          item.available <= 0
                            ? 'bg-gradient-danger'
                            : 'bg-gradient-warning'
                        "
                      >
                        {{ item.available }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-5">
        <timeline-list
          title="Ostatnia aktywność"
          description="Najważniejsze zdarzenia systemu"
        >
          <timeline-item
            v-for="(item, index) in dashboardData.activity"
            :key="index"
            :title="item.title"
            :date-time="item.dateTime"
            :description="item.description"
            :color="item.color"
            :icon="item.icon"
            :badges="item.badges"
          />
        </timeline-list>
      </div>
    </div>

    <!-- ================================================================
         FOOTER SUMMARY
    ================================================================= -->

    <div class="row mt-4">
      <div class="col-12">
        <div class="card bg-gradient-dark">
          <div class="card-body p-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <h5 class="text-white mb-1">
                  SalesCenter — stan operacyjny
                </h5>

                <p class="text-white opacity-8 mb-0">
                  {{ number(availableStock) }} wariantów posiada aktualny
                  zapas · {{ dashboardData.production.inProgress }} produkcji
                  w toku · {{ dashboardData.orders.ready }} zamówień gotowych
                  do wysyłki.
                </p>
              </div>

              <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
                <span
                  class="badge bg-gradient-warning me-2"
                  v-if="dashboardData.inventory.lowStock > 0"
                >
                  {{ dashboardData.inventory.lowStock }} niski stan
                </span>

                <span
                  class="badge bg-gradient-danger"
                  v-if="dashboardData.production.failed > 0"
                >
                  {{ dashboardData.production.failed }} błąd produkcji
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>